import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Zap, Calendar, MessageSquare, Star, Car, Wrench, FileText,
  Shield, Camera, AlertTriangle, Settings, ClipboardList
} from "lucide-react";

const AIReceptionistAutoBodyPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Auto Body Shops Dallas | Auto Body Virtual Receptionist | Collision Repair Answering Service";
  }, []);

  const stats = [
    { value: "24/7", label: "Estimate Requests", icon: Clock },
    { value: "Estimates", label: "Captured 24/7", icon: ClipboardList },
    { value: "100%", label: "Calls Answered", icon: PhoneCall },
    { value: "<30s", label: "Response Time", icon: Zap }
  ];

  const autoServices = [
    { icon: Car, name: "Collision Repair", desc: "Accident estimates, insurance claims, repair scheduling" },
    { icon: Wrench, name: "Dent Removal", desc: "PDR quotes, hail damage, door ding repairs" },
    { icon: FileText, name: "Insurance Claims", desc: "Direct repair program, supplement handling, adjuster scheduling" },
    { icon: Camera, name: "Estimate Scheduling", desc: "In-person estimates, photo estimates, walk-in management" },
    { icon: Settings, name: "Status Updates", desc: "Repair progress, part arrivals, completion dates" },
    { icon: Shield, name: "Warranty Work", desc: "Warranty claims, touch-ups, quality callbacks" }
  ];

  const features = [
    { 
      icon: Car, 
      title: "24/7 Estimate Booking", 
      desc: "Accident victims need estimates fast. AI captures damage details, insurance info, and books estimates 24/7—even from the accident scene." 
    },
    { 
      icon: FileText, 
      title: "Insurance Coordination", 
      desc: "Verifies insurance, explains DRP benefits, schedules adjuster meetings, and answers coverage questions. Streamlines the entire claim process." 
    },
    { 
      icon: MessageSquare, 
      title: "Status Update Automation", 
      desc: "Customers calling for updates? AI provides real-time repair status, part arrival info, and estimated completion—freeing your staff from repetitive calls." 
    },
    { 
      icon: Camera, 
      title: "Photo Estimate Intake", 
      desc: "Guides customers through submitting damage photos via text for preliminary estimates before they even come in." 
    },
    { 
      icon: Calendar, 
      title: "Rental Car Coordination", 
      desc: "Schedules rental car pickup, coordinates with Enterprise/Hertz, and keeps customers mobile throughout repairs." 
    },
    { 
      icon: Shield, 
      title: "Warranty & Follow-Up", 
      desc: "Handles warranty questions, schedules touch-ups, and manages quality callbacks to ensure customer satisfaction." 
    }
  ];

  const comparisons = [
    { item: "Front Office Staff", traditional: "$35,000-$45,000/year", ai: "$199-$399/month" },
    { item: "After-Hours Calls", traditional: "Lost to competitors", ai: "100% captured 24/7" },
    { item: "Status Call Time", traditional: "5-10 min/call", ai: "Automated instant" },
    { item: "Insurance Verification", traditional: "Manual lookup", ai: "Immediate capture" },
    { item: "Spanish-Speaking", traditional: "Limited/None", ai: "Fluent Spanish" },
    { item: "Multi-Shop Support", traditional: "Staff per location", ai: "One AI, all shops" }
  ];

  const faqs = [
    { 
      q: "How does AI handle calls from accident scenes?", 
      a: "When someone calls from an accident, they're stressed and need immediate help. Our AI provides calm, step-by-step guidance: ensuring safety first, capturing damage description, gathering insurance info, and booking the earliest available estimate time. It can even text directions to your shop and arrange towing if needed. This immediate response often wins the job before they call competitors."
    },
    { 
      q: "Can AI manage insurance claim coordination?",
      a: "Yes. The AI verifies insurance coverage, explains your Direct Repair Program benefits, captures claim numbers, and schedules adjuster inspections. It answers common questions about deductibles, rental coverage, and the claims process. For complex insurance situations, it captures all details and schedules a callback from your estimator with full context."
    },
    { 
      q: "How does it handle constant status update calls?",
      a: "Status calls can consume 30-40% of front office time. The AI integrates with shop management systems (CCC ONE, Mitchell, Audatex) to provide real-time repair status, part arrival updates, and estimated completion dates. Customers get instant answers, and your staff can focus on higher-value work."
    },
    { 
      q: "Does it work with shop management software?",
      a: "We integrate with CCC ONE, Mitchell Cloud, Audatex, and other leading collision repair management platforms. Estimates are scheduled, status is pulled automatically, and all customer interactions are logged. No double-entry, no information gaps."
    },
    { 
      q: "What about photo estimates?",
      a: "The AI guides customers through taking and submitting damage photos via text message. It explains what angles are needed, ensures photos are clear enough for preliminary assessment, and routes them to your estimators. This pre-qualifies leads and speeds up the in-person estimate process."
    },
    { 
      q: "Can it help with customer retention?",
      a: "Absolutely. The AI can send post-repair follow-ups to ensure satisfaction, schedule warranty touch-ups if needed, and even remind customers about annual paint protection reapplication or other services. Happy customers become referral sources—and the AI captures those too."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Auto Body Shops",
        "description": "24/7 AI-powered virtual receptionist for auto body shops and collision repair centers in Dallas-Fort Worth.",
        "provider": { "@type": "LocalBusiness", "name": "Digital Media Ninja" },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney"],
        "serviceType": "AI Virtual Receptionist for Auto Body"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Auto Body Receptionist",
        "offers": { "@type": "AggregateOffer", "lowPrice": "199", "highPrice": "399", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-auto-body-page">
      <Helmet>
        <title>AI Receptionist for Auto Body Shops Dallas | Collision Repair Answering Service | Virtual Receptionist</title>
        <meta name="description" content="AI receptionist for Dallas auto body shops. 24/7 estimate booking, insurance coordination, status updates. CCC ONE & Mitchell integration. Capture more collision repairs. Free demo!" />
        <meta name="keywords" content="AI receptionist auto body Dallas, collision repair answering service, auto body virtual receptionist, body shop phone answering, collision center AI, auto body estimate booking" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-auto-body" />
        <meta property="og:title" content="AI Receptionist for Auto Body Shops Dallas | 24/7 Estimate Booking" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-purple-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full">
                <Car className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Built for Collision Repair</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-purple-400">Auto Body Shops</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Capture every estimate request. AI handles calls 24/7, coordinates insurance, and provides status updates—even while you're repairing cars.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="general"
                  size="lg"
                  className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Car className="w-8 h-8 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Collision Repair AI</h3>
                      <p className="text-purple-400">24/7 Estimate Booking</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["CCC ONE & Mitchell integration", "Insurance claim coordination", "Real-time status updates", "Photo estimate intake"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats, Services, Features, Comparison, Testimonials, FAQ sections follow same pattern */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Auto Body Services We Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {autoServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-purple-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-purple-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Auto Body Shops</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-purple-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-purple-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-purple-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-purple-600/20 to-purple-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Capture More Repairs?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas auto body shops using AI to book more estimates and streamline operations.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistAutoBodyPage;
